<template>
  <section v-if="loading || tescoPortalStubsEnabled">
    <page-sub-header title="Coach" :subtitle="isDataReady ? timeSeriesSubtitle : undefined" class="mt-4">
      <template #header>
        <div class="d-flex flex-row align-items-center justify-content-end">
          <app-input
            v-model="timeSeries"
            :options="TIME_SERIES_VALUES"
            type="radio"
            buttons
            button-variant="outline-primary"
            class="text-uppercase mb-0"
            size="sm"
          />
          <export-button
            v-if="isDataReady"
            :file-name="`coach-metrics-${timeSeries}`"
            :export-data="exportData"
            :export-file-rows="exportFileRows"
            :export-headers="exportHeaders"
            class="btn mt-0"
          />
        </div>
      </template>
    </page-sub-header>
    <sme-card v-if="loading">
      <app-loading />
    </sme-card>
    <template v-else-if="tescoPortalStubsEnabled">
      <template v-if="timeGroupedCoachConversationsDataset.values.length">
        <chart-with-companion-kpis
          card-class="mb-4"
          chart-title="Coach conversations over time"
          chart-subtitle="Number of conversations"
        >
          <line-chart
            :labels="timeGroupDataset"
            :datasets="timeGroupedCoachConversationsDataset"
            :chart-options="{
              aspectRatio: getInitialAspectRatio(3, 2),
              scales: {
                y: { ticks: { precision: 0 }, title: { text: timeGroupedCoachConversationsDataset.name } },
              },
            }"
            fill-gradient
          />
          <template #companion>
            <app-stat
              :stat="lastGroupedCoachConversations"
              :previous-stat="prevGroupedCoachConversations"
              :formatter="toInteger"
              :description="`${timeSeriesUnit === 'week' ? 'Weekly' : 'Monthly'} Coach Conversations`"
              class="mb-3"
              background-color="core-1-lighten-90"
              color="default"
              pdf-kpi
            />
            <p class="mb-0">
              {{ timeGroupTitle }}, {{ toInteger(lastGroupedCoachConversations) }} of your employees had a coach
              conversation.
              {{ coachConversationsPrevDiffText }}
            </p>
          </template>
        </chart-with-companion-kpis>

        <chart-with-companion-kpis
          card-class="mb-4"
          chart-title="Coach conversations percentage over time"
          chart-subtitle="Percentage (%)"
        >
          <line-chart
            :labels="timeGroupDataset"
            :datasets="timeGroupedCoachConversationsPercentageDataset"
            :chart-options="{
              aspectRatio: getInitialAspectRatio(3, 2),
              scales: { y: { title: { text: timeGroupedCoachConversationsPercentageDataset.name } } },
            }"
            fill-gradient
          />
          <template #companion>
            <app-stat
              :stat="lastGroupedCoachConversationsPercentage"
              :previous-stat="prevGroupedCoachConversationsPercentage"
              :formatter="toNumber"
              suffix="%"
              :description="`${timeSeriesUnit === 'week' ? 'Weekly' : 'Monthly'} Coach Conversations Percentage`"
              class="mb-3"
              background-color="core-1-lighten-90"
              color="default"
              pdf-kpi
            />
            <p class="mb-0">
              {{ timeGroupTitle }}, {{ lastGroupedCoachConversationsPercentage }}% of your enrolled employees had a
              coach conversation.
              {{ coachConversationsPercentagePrevDiffText }}
            </p>
          </template>
        </chart-with-companion-kpis>
      </template>

      <!-- Row 1: Coach metrics -->
      <b-row>
        <b-col cols="4" lg="4" class="mb-4">
          <sme-card class="h-100 overflow-hidden" v-pdf-chart-group>
            <app-stat
              :stat="financialHealthScoresDataset.calculated.mean || 0"
              :formatter="value => toInteger(Math.round(value))"
              description="Average financial health score"
              :class="[financialHealthScoresDataset.values.length && 'mb-3']"
              background-color="core-5-lighten-90"
              color="default"
              small
              pdf-kpi
            />
            <pdf-chart title="Financial health scores">
              <line-chart
                v-if="financialHealthScoresDataset.values.length"
                :labels="timeGroupDataset"
                :datasets="financialHealthScoresDataset"
                :chart-options="merge({}, CHART_PRESET_OPTIONS.MINIMAL)"
                :display-points="false"
                class="mt-auto"
                fill-gradient
              />
            </pdf-chart>
          </sme-card>
        </b-col>
        <b-col cols="4" lg="4" class="mb-4">
          <sme-card class="h-100 overflow-hidden" v-pdf-chart-group>
            <app-stat
              :stat="stepchangeReferralsDataset.calculated.mean || 0"
              :formatter="value => toInteger(Math.round(value))"
              :description="`Average ${timeSeriesUnit}ly Stepchange referrals`"
              :class="[stepchangeReferralsDataset.values.length && 'mb-3']"
              background-color="core-4-lighten-90"
              color="default"
              small
              pdf-kpi
            />
            <pdf-chart title="Stepchange referrals">
              <line-chart
                v-if="stepchangeReferralsDataset.values.length"
                :labels="timeGroupDataset"
                :datasets="stepchangeReferralsDataset"
                :chart-options="merge({}, CHART_PRESET_OPTIONS.MINIMAL)"
                :display-points="false"
                class="mt-auto"
                fill-gradient
              />
            </pdf-chart>
          </sme-card>
        </b-col>
        <b-col cols="4" lg="4" class="mb-4">
          <sme-card class="h-100 overflow-hidden" v-pdf-chart-group>
            <app-stat
              :stat="benefitCalculatorDataset.calculated.mean || 0"
              :formatter="value => toInteger(Math.round(value))"
              :description="`Average ${timeSeriesUnit}ly benefit calculator completions (£347 average estimated benefits uncovered)`"
              :class="[benefitCalculatorDataset.values.length && 'mb-3']"
              background-color="core-3-lighten-80"
              color="default"
              small
              pdf-kpi
            />
            <pdf-chart title="Benefit calculator completions">
              <line-chart
                v-if="benefitCalculatorDataset.values.length"
                :labels="timeGroupDataset"
                :datasets="benefitCalculatorDataset"
                :chart-options="merge({}, CHART_PRESET_OPTIONS.MINIMAL)"
                :display-points="false"
                class="mt-auto"
                fill-gradient
              />
            </pdf-chart>
          </sme-card>
        </b-col>
      </b-row>
    </template>
  </section>
</template>

<script setup>
import merge from 'lodash/merge';
import { computed, onBeforeMount, ref, watch } from 'vue';
import ApiClient from '@/ApiClient';
import { PaletteColors } from '@/Theme';
import AppInput from '@/components/AppInput.vue';
import AppLoading from '@/components/AppLoading.vue';
import AppStat from '@/components/AppStat.vue';
import ChartWithCompanionKpis from '@/components/ChartWithCompanionKpis.vue';
import PageSubHeader from '@/components/PageSubHeader.vue';
import PdfChart from '@/components/PdfChart.vue';
import SmeCard from '@/components/atoms/SmeCard.vue';
import LineChart, { CHART_PRESET_OPTIONS } from '@/components/charts/LineChart.vue';
import useFeatureFlags from '@/composables/useFeatureFlags';
import useTimeGroupDataset from '@/composables/useTimeGroupDataset';
import useDivisionSelection from '@/state/composables/useDivisionSelection';
import { getInitialAspectRatio, TIME_SERIES_VALUES } from '@/utils/chart';
import { toInteger, toNumber } from '@/utils/common';
import ExportButton from '@/components/ExportButton.vue';

const { tescoPortalStubsEnabled } = useFeatureFlags();
const { divisionId } = useDivisionSelection();

const loading = ref(true);
const timeDataset = ref(null);
const coachConversationsDataset = ref(null);
const coachConversationsPercentageDataset = ref(null);
const financialHealthScoresDataset = ref(null);
const stepchangeReferralsDataset = ref(null);
const benefitCalculatorDataset = ref(null);
const trackUsersDataset = ref(null); // To get track users count

const {
  timeGroupDataset,
  timeGroupTitle,
  timeSeries,
  timeSeriesUnit,
  timeSeriesSubtitle,
  datasetToTimeGroupedDataset,
  getPrevDiffText,
} = useTimeGroupDataset(timeDataset);

const timeGroupedCoachConversationsDataset = computed(() => datasetToTimeGroupedDataset(coachConversationsDataset));
const timeGroupedCoachConversationsPercentageDataset = computed(() =>
  datasetToTimeGroupedDataset(coachConversationsPercentageDataset),
);
const lastGroupedCoachConversations = computed(() => timeGroupedCoachConversationsDataset.value?.calculated.last);
const prevGroupedCoachConversations = computed(() => timeGroupedCoachConversationsDataset.value?.calculated.prev);
const lastGroupedCoachConversationsPercentage = computed(
  () => timeGroupedCoachConversationsPercentageDataset.value?.calculated.last,
);
const prevGroupedCoachConversationsPercentage = computed(
  () => timeGroupedCoachConversationsPercentageDataset.value?.calculated.prev,
);
const coachConversationsPrevDiffText = computed(() => getPrevDiffText(timeGroupedCoachConversationsDataset));
const coachConversationsPercentagePrevDiffText = computed(() =>
  getPrevDiffText(timeGroupedCoachConversationsPercentageDataset, value => `${Number(toNumber(value))}%`),
);

watch([divisionId, timeSeries], () => getCoachMetrics());

const getCoachMetrics = async () => {
  loading.value = true;

  if (!tescoPortalStubsEnabled.value) {
    loading.value = false;
    return;
  }

  // Get track metrics to calculate coach conversations based on track users
  const [trackResults] = await Promise.all([ApiClient.getTrackMetrics(timeSeries.value, divisionId.value)]);

  // Generate fake time dataset (use track's time if available)
  if (trackResults?.results?.length) {
    const time = trackResults.results.find(result => result.id === 'time');
    const trackUsers = trackResults.results.find(result => result.id === 'track_users');
    const enrolled = trackResults.results.find(result => result.id === 'enrolled');

    if (time && trackUsers && enrolled) {
      timeDataset.value = time;
      trackUsersDataset.value = trackUsers;

      // Scale factor for weekly vs monthly (scale weekly down by 4.3x)
      const timeSeriesMultiplier = timeSeries.value === 'w2w' ? 1 / 4.3 : 1; // Scale weekly down

      // Generate fake coach conversations data (0-5% of enrolled employees)
      const conversationPercentages = time.values.map(() => {
        // Generate percentage between 0-5%, relatively flat
        const basePercentage = 2.5; // Center around 2.5%
        const variation = (Math.random() - 0.5) * 4; // ±2%
        return Math.max(0, Math.min(5, Math.round((basePercentage + variation) * 10) / 10));
      });

      // Calculate actual conversation counts (percentage * track users * time multiplier)
      const conversationCounts = conversationPercentages.map((percentage, index) => {
        const trackUserCount = trackUsers.values[index] || 0;
        return Math.round((percentage * trackUserCount * timeSeriesMultiplier) / 100);
      });

      coachConversationsDataset.value = {
        name: 'Number of coach conversations',
        values: conversationCounts,
        lineColor: PaletteColors['core-1'],
      };

      coachConversationsPercentageDataset.value = {
        name: 'Percentage (%)',
        values: conversationPercentages,
        lineColor: PaletteColors['core-1'],
      };

      // Generate financial health scores (15-25, around 20)
      const healthScores = time.values.map(() => {
        const baseScore = 20;
        const variation = (Math.random() - 0.5) * 8; // ±4
        return Math.max(15, Math.min(25, Math.round(baseScore + variation)));
      });

      financialHealthScoresDataset.value = {
        name: 'Health Score',
        values: healthScores,
        lineColor: PaletteColors['core-5'],
        calculated: {
          mean: healthScores.reduce((a, b) => a + b, 0) / healthScores.length,
        },
      };

      // Generate Stepchange referrals (1/3 of average coach conversations)
      const avgConversations = conversationCounts.reduce((a, b) => a + b, 0) / conversationCounts.length;
      const stepchangeReferrals = time.values.map(() => {
        const baseReferrals = avgConversations / 3;
        const variation = (Math.random() - 0.5) * baseReferrals; // ±50%
        return Math.max(0, Math.round(baseReferrals + variation));
      });

      stepchangeReferralsDataset.value = {
        name: 'Stepchange Referrals',
        values: stepchangeReferrals,
        lineColor: PaletteColors['core-4'],
        calculated: {
          mean: stepchangeReferrals.reduce((a, b) => a + b, 0) / stepchangeReferrals.length,
        },
      };

      // Generate benefit calculator completions (1/2 of average coach conversations)
      const benefitCalculatorCompletions = time.values.map(() => {
        const baseCompletions = avgConversations / 2;
        const variation = (Math.random() - 0.5) * baseCompletions; // ±50%
        return Math.max(0, Math.round(baseCompletions + variation));
      });

      benefitCalculatorDataset.value = {
        name: 'Benefit Calculator Completions',
        values: benefitCalculatorCompletions,
        lineColor: PaletteColors['core-3'],
        calculated: {
          mean: benefitCalculatorCompletions.reduce((a, b) => a + b, 0) / benefitCalculatorCompletions.length,
        },
      };
    }
  }

  loading.value = false;
};

onBeforeMount(() => getCoachMetrics());

const isDataReady = computed(() => {
  if (!tescoPortalStubsEnabled.value) {
    return false;
  }
  return (
    timeGroupDataset.value?.values &&
    coachConversationsDataset.value?.values &&
    coachConversationsPercentageDataset.value?.values &&
    financialHealthScoresDataset.value?.values &&
    stepchangeReferralsDataset.value?.values &&
    benefitCalculatorDataset.value?.values &&
    !loading.value
  );
});

const exportData = computed(() => {
  if (!isDataReady.value) {
    return [];
  }

  return timeGroupDataset.value.values.map((time, index) => ({
    time,
    conversations: coachConversationsDataset.value.values[index],
    percentage: coachConversationsPercentageDataset.value.values[index],
    healthScore: financialHealthScoresDataset.value.values[index],
    stepchangeReferrals: stepchangeReferralsDataset.value.values[index],
    benefitCalculator: benefitCalculatorDataset.value.values[index],
  }));
});

const exportHeaders = computed(() => {
  if (!isDataReady.value) {
    return [];
  }
  return [
    'Date',
    coachConversationsDataset.value.name,
    coachConversationsPercentageDataset.value.name,
    'Financial Health Score',
    stepchangeReferralsDataset.value.name,
    benefitCalculatorDataset.value.name,
  ];
});

const exportFileRows = item => [
  item.time,
  item.conversations,
  item.percentage,
  item.healthScore,
  item.stepchangeReferrals,
  item.benefitCalculator,
];
</script>
